import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2, Building2, MessageSquare } from 'lucide-react';
import { api } from './api';
import { useAuth } from './AuthContext';
import DealMessagesPanel from './DealMessagesPanel';
import { BRAND } from './kit/tokens';
import { addressLine } from './shared/address';
import { rememberPostLoginNext } from './postLoginNext';
import { isExternalRole, homeForRole } from './externalRoles';


// /deal/:dealId — the page the "you were added to a deal" email links to.
export default function ExternalDealPage() {
  const { dealId } = useParams();
  const [params] = useSearchParams();
  const invite = params.get('invite');
  const { user, loading } = useAuth();
  const external = !!user && isExternalRole(user.role);

  useEffect(() => {
    if (!loading && !user) rememberPostLoginNext(`/deal/${dealId}`);
  }, [loading, user, dealId]);

  if (loading) return <Spinner />;
  if (!user) {
    return invite
      ? <Navigate to={`/portal/invite?invite=${encodeURIComponent(invite)}`} replace />
      : <Navigate to="/portal/login" replace />;
  }
  if (!external) return <Navigate to={`/portal/deals/${encodeURIComponent(dealId)}`} replace />;
  return <ExternalDeal dealId={dealId} user={user} />;
}

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="animate-spin text-slate-400" />
    </div>
  );
}

function ExternalDeal({ dealId, user }) {
  const { logout } = useAuth();
  const [summary, setSummary] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let live = true;
    api.get(`/deals/${encodeURIComponent(dealId)}/status`)
      .then(({ data }) => { if (live) setSummary(data); })
      .catch(() => { if (live) setUnavailable(true); });
    return () => { live = false; };
  }, [dealId]);

  if (unavailable) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" data-testid="external-deal-unavailable">
        <div className="max-w-md text-center">
          <h1 className="font-serif text-2xl font-bold text-slate-900">This deal isn't available to this account.</h1>
          <p className="mt-2 text-sm text-slate-600">Signed in as {user.email}. Sign in with the email address the deal was sent to.</p>
          <div className="mt-4 flex justify-center gap-4 text-sm">
            <Link to={homeForRole(user.role)} className="underline">Back to your portal</Link>
            <button type="button" className="underline" onClick={async () => { await logout(); window.location.assign('/portal/login'); }}>Sign out</button>
          </div>
        </div>
      </div>
    );
  }
  if (!summary) return <Spinner />;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="external-deal-page">
      <header className="border-b bg-white sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center bg-brand-navy">
            <Building2 size={18} color="white" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Your deal</div>
            <div className="font-serif text-lg font-bold text-brand-navy">Hi {user.name || 'there'}</div>
          </div>
          <Link to={homeForRole(user.role)} state={{ stay: true }} className="text-sm text-slate-600 underline">Your portal</Link>
        </div>
      </header>
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 space-y-5">
        <div className="rounded-2xl p-6 text-white grid md:grid-cols-3 gap-4" style={{ background: `linear-gradient(160deg, ${BRAND.navyDeep} 0%, ${BRAND.navy} 60%, ${BRAND.navyLight} 100%)` }} data-testid="deal-summary">
          <Field label="Property" value={addressLine(summary.property_address) || 'Being finalized'} />
          <Field label="Realtor" value={summary.agent_name || 'Assigned soon'} />
          <Field label="Loan Officer" value={summary.lo_name || 'Assigned soon'} />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare size={14} className="text-brand-navy" />
            <h2 className="font-serif text-lg font-bold text-brand-navy">Chat with your team</h2>
          </div>
          <DealMessagesPanel
            apiClient={api}
            dealId={dealId}
            currentUserId={user.user_id}
            currentUserRole={user.role}
            navy={BRAND.navy}
            green={BRAND.accent}
          />
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-brand-accent">{label}</div>
      <div className="font-serif text-lg font-semibold mt-0.5">{value}</div>
    </div>
  );
}
